/**
 * Normalizes the fare level `inclusions` and `penalties` returned in the
 * flight search response into a single, UI friendly shape.
 *
 * Nothing is hardcoded here — a feature is only returned when the response
 * (or the caller supplied fallback) actually carries information for it.
 *
 * Response shapes handled:
 *  fare.inclusions: [{ descType, descText, isIncluded, isChargeable }]
 *    - isIncluded / isChargeable may be "true" | "false" | "NA" | boolean
 *  fare.penalties:  [{ penaltyType, amount, currency, paxType,
 *                      penaltyApplies, penaltyText, durationRange, tripNumber }]
 */
import {
  formatCurrency,
  formatCurrencyWithoutSymbol,
  isEmpty,
} from './appUtils';
import { getValueFromAppConfig } from './localStorage';

export const FARE_FEATURE_TYPES = {
  CANCELLATION: 'cancellation',
  RESCHEDULE: 'reschedule',
  MEAL: 'meal',
  SEAT: 'seat',
  CHECKIN_BAGGAGE: 'checkinBaggage',
  CABIN_BAGGAGE: 'cabinBaggage',
};

export const FARE_FEATURE_STATUS = {
  FREE: 'free',
  CHARGEABLE: 'chargeable',
  NOT_ALLOWED: 'notAllowed',
  INFO: 'info',
};

const FEATURE_LABELS = {
  [FARE_FEATURE_TYPES.CANCELLATION]: 'Cancellation',
  [FARE_FEATURE_TYPES.RESCHEDULE]: 'Reschedule',
  [FARE_FEATURE_TYPES.MEAL]: 'Meal',
  [FARE_FEATURE_TYPES.SEAT]: 'Seat',
};

const STATUS_LABELS = {
  [FARE_FEATURE_STATUS.FREE]: 'Free',
  [FARE_FEATURE_STATUS.CHARGEABLE]: 'Chargeable',
  [FARE_FEATURE_STATUS.NOT_ALLOWED]: 'Not Allowed',
};

// Display order of the feature chips on the fare card.
const FEATURE_ORDER = [
  FARE_FEATURE_TYPES.CANCELLATION,
  FARE_FEATURE_TYPES.RESCHEDULE,
  FARE_FEATURE_TYPES.MEAL,
  FARE_FEATURE_TYPES.SEAT,
];

const normalizeKey = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');

const INCLUSION_TYPE_ALIASES = {
  cancel: FARE_FEATURE_TYPES.CANCELLATION,
  cancellation: FARE_FEATURE_TYPES.CANCELLATION,
  refund: FARE_FEATURE_TYPES.CANCELLATION,
  change: FARE_FEATURE_TYPES.RESCHEDULE,
  datechange: FARE_FEATURE_TYPES.RESCHEDULE,
  reschedule: FARE_FEATURE_TYPES.RESCHEDULE,
  reissue: FARE_FEATURE_TYPES.RESCHEDULE,
  meal: FARE_FEATURE_TYPES.MEAL,
  meals: FARE_FEATURE_TYPES.MEAL,
  seat: FARE_FEATURE_TYPES.SEAT,
  seats: FARE_FEATURE_TYPES.SEAT,
  seatselection: FARE_FEATURE_TYPES.SEAT,
  checkinbaggage: FARE_FEATURE_TYPES.CHECKIN_BAGGAGE,
  checkin: FARE_FEATURE_TYPES.CHECKIN_BAGGAGE,
  checkinbag: FARE_FEATURE_TYPES.CHECKIN_BAGGAGE,
  cabinbaggage: FARE_FEATURE_TYPES.CABIN_BAGGAGE,
  cabinbag: FARE_FEATURE_TYPES.CABIN_BAGGAGE,
  cabin: FARE_FEATURE_TYPES.CABIN_BAGGAGE,
  handbaggage: FARE_FEATURE_TYPES.CABIN_BAGGAGE,
};

const getInclusionType = (descType) =>
  INCLUSION_TYPE_ALIASES[normalizeKey(descType)] || null;

const getPenaltyType = (penaltyType) => {
  const key = normalizeKey(penaltyType);
  if (key.includes('cancel') || key.includes('refund')) {
    return FARE_FEATURE_TYPES.CANCELLATION;
  }
  if (
    key.includes('change') ||
    key.includes('reissue') ||
    key.includes('reschedule')
  ) {
    return FARE_FEATURE_TYPES.RESCHEDULE;
  }
  return null;
};

/** "true" / true -> true, "false" / false -> false, anything else -> null */
const toTriState = (value) => {
  if (typeof value === 'boolean') return value;
  const key = normalizeKey(value);
  if (key === 'true' || key === 'yes' || key === 'y') return true;
  if (key === 'false' || key === 'no' || key === 'n') return false;
  return null;
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return [value];
  return [];
};

const toFiniteNumber = (value) => {
  if (isEmpty(value)) return null;
  const num = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(num) ? num : null;
};

const formatAmount = (amount, currency) => {
  if (Number(amount) === -1) {
    return 'REFER FARE RULES';
  }
  const appCurrency = getValueFromAppConfig('SAAS_APP_CURRENCY');
  if (isEmpty(currency) || currency === appCurrency) {
    return formatCurrency(amount);
  }
  return formatCurrencyWithoutSymbol(amount, currency);
};

/** Extracts "INR 4999.0" / "₹ 4,999" style amounts from free text. */
const parseAmountFromText = (text) => {
  const match = String(text ?? '').match(
    /(?:([A-Z]{3})|₹|Rs\.?)\s*([\d,]+(?:\.\d+)?)/,
  );
  if (!match) return null;
  const amount = toFiniteNumber(match[2]);
  if (amount === null) return null;
  return { amount, currency: match[1] || '' };
};

const NOT_ALLOWED_REGEX =
  /\b(not\s+(included|allowed|permitted|available|applicable)|non[\s-]?(refundable|changeable)|no\s+(meal|seat|baggage|bag))\b/i;
const FREE_REGEX =
  /\b(free|complimentary|included|no\s+(charge|fee)s?|nil|waived)\b/i;
const CHARGEABLE_REGEX =
  /\b(charge|charges|chargeable|fee|fees|paid|penalty)\b/i;

const inferStatusFromText = (text) => {
  if (isEmpty(text)) return null;
  if (NOT_ALLOWED_REGEX.test(text)) return FARE_FEATURE_STATUS.NOT_ALLOWED;
  if (FREE_REGEX.test(text)) return FARE_FEATURE_STATUS.FREE;
  if (CHARGEABLE_REGEX.test(text)) return FARE_FEATURE_STATUS.CHARGEABLE;
  return null;
};

const getStatusFromFlags = (isIncluded, isChargeable) => {
  if (isIncluded === true) return FARE_FEATURE_STATUS.FREE;
  if (isChargeable === true) return FARE_FEATURE_STATUS.CHARGEABLE;
  if (isIncluded === false && isChargeable === false) {
    return FARE_FEATURE_STATUS.NOT_ALLOWED;
  }
  return null;
};

/** "15 KG Check-in bag allowance" -> "15KG", "1 Piece" -> "1PC" */
export const parseBaggageFromText = (text) => {
  const match = String(text ?? '').match(
    /(\d+(?:\.\d+)?)\s*(kgs?|kilograms?|pcs?|pieces?|lbs?)\b/i,
  );
  if (!match) return '';
  const unitKey = match[2].toLowerCase();
  let unit = 'KG';
  if (unitKey.startsWith('p')) unit = 'PC';
  else if (unitKey.startsWith('l')) unit = 'LB';
  return `${Number(match[1])}${unit}`;
};

const isAdultOrAllPax = (paxType) => {
  const key = normalizeKey(paxType);
  return key === '' || key === 'all' || key === 'adt' || key === 'adult';
};

/**
 * Picks the penalties of one feature type. Adult / "all" pax entries are
 * preferred because the fare card is shown per adult.
 */
const getPenaltiesForType = (penalties, type) => {
  const ofType = penalties.filter(
    (p) => getPenaltyType(p?.penaltyType) === type,
  );
  const adultOrAll = ofType.filter((p) => isAdultOrAllPax(p?.paxType));
  return adultOrAll.length > 0 ? adultOrAll : ofType;
};

const getPenaltySummary = (penalties, fare) => {
  const withAmount = penalties
    .map((p) => ({ ...p, amount: toFiniteNumber(p?.amount) }))
    .filter((p) => p.amount !== null);
  if (withAmount.length === 0) return null;

  const maxPenalty = withAmount.reduce((max, p) =>
    p.amount > max.amount ? p : max,
  );
  const hasPenaltyText = withAmount.some((p) => !isEmpty(p.penaltyText));

  // Suppliers send 0 without any description when the penalty is unknown.
  // Treat that as "no information" rather than claiming it is free.
  if (maxPenalty.amount === 0 && !hasPenaltyText) return null;

  const details = withAmount.map((p) => {
    const applies = !isEmpty(p.penaltyApplies)
      ? p.penaltyApplies
      : p.durationRange;
    const amountText =
      p.amount === 0 ? 'Free' : formatAmount(p.amount, p.currency);
    return isEmpty(applies) ? amountText : `${applies}: ${amountText}`;
  });
  const penaltyText = withAmount.find((p) => !isEmpty(p.penaltyText));
  if (penaltyText) details.push(penaltyText.penaltyText);

  if (maxPenalty.amount === 0) {
    return { status: FARE_FEATURE_STATUS.FREE, value: 'Free', details };
  }
  if (maxPenalty.amount === -1) {
    return {
      status: FARE_FEATURE_STATUS.CHARGEABLE,
      value: 'REFER FARE RULES',
      details: details.filter((d) => d && d !== 'REFER FARE RULES'),
    };
  }

  const uniqueAmounts = new Set(withAmount.map((p) => p.amount));
  const amountText = formatAmount(
    maxPenalty.amount,
    maxPenalty.currency || fare?.currency,
  );
  return {
    status: FARE_FEATURE_STATUS.CHARGEABLE,
    value: uniqueAmounts.size > 1 ? `Upto ${amountText}` : amountText,
    details,
  };
};

const getInclusionSummary = (inclusion, type) => {
  const text = String(inclusion?.descText ?? '').trim();
  if (text === '-1') {
    return {
      status: FARE_FEATURE_STATUS.CHARGEABLE,
      value: 'REFER FARE RULES',
      details: [],
    };
  }
  const flagStatus = getStatusFromFlags(
    toTriState(inclusion?.isIncluded),
    toTriState(inclusion?.isChargeable),
  );
  const parsedAmount =
    type === FARE_FEATURE_TYPES.CANCELLATION ||
    type === FARE_FEATURE_TYPES.RESCHEDULE
      ? parseAmountFromText(text)
      : null;

  let status = flagStatus || inferStatusFromText(text);
  let value = '';

  if (parsedAmount && status !== FARE_FEATURE_STATUS.NOT_ALLOWED) {
    if (parsedAmount.amount === 0) {
      status = FARE_FEATURE_STATUS.FREE;
    } else {
      status = FARE_FEATURE_STATUS.CHARGEABLE;
      const amountText = formatAmount(
        parsedAmount.amount,
        parsedAmount.currency,
      );
      value = /\bup\s*to\b/i.test(text) ? `Upto ${amountText}` : amountText;
    }
  }

  if (!status && isEmpty(text)) return null;
  if (!status) status = FARE_FEATURE_STATUS.INFO;
  if (!value) {
    value =
      status === FARE_FEATURE_STATUS.NOT_ALLOWED &&
      (type === FARE_FEATURE_TYPES.MEAL || type === FARE_FEATURE_TYPES.SEAT)
        ? 'Not Included'
        : STATUS_LABELS[status] || text;
  }

  return {
    status,
    value,
    details: text && text !== value ? [text] : [],
  };
};

/**
 * Legacy fare inclusion map entry (FareInclusions API) keyed by
 * Cancellation / Reissue / MealIncluded / SeatIncluded / Checkin / HandBaggage.
 */
const LEGACY_KEYS = {
  [FARE_FEATURE_TYPES.CANCELLATION]: 'Cancellation',
  [FARE_FEATURE_TYPES.RESCHEDULE]: 'Reissue',
  [FARE_FEATURE_TYPES.MEAL]: 'MealIncluded',
  [FARE_FEATURE_TYPES.SEAT]: 'SeatIncluded',
};

const getLegacySummary = (legacyInclusion, type) => {
  const text = legacyInclusion?.[LEGACY_KEYS[type]];
  if (isEmpty(text) || typeof text !== 'string') return null;
  return getInclusionSummary({ descText: text }, type);
};

/**
 * @param {object} fare  flightfare object from the search response
 * @param {object} [options]
 * @param {string} [options.checkinFallback] baggage shown when inclusions have none
 * @param {string} [options.cabinFallback]   baggage shown when inclusions have none
 * @param {object} [options.legacyInclusion] entry of the legacy FareInclusions map
 * @returns {{ features: Array<{type, label, status, value, details}>,
 *             baggage: { checkin: string, cabin: string } }}
 */
export const getFareInclusionDetails = (fare, options = {}) => {
  const { checkinFallback = '', cabinFallback = '', legacyInclusion } = options;
  const inclusions = toArray(fare?.inclusions);
  const penalties = toArray(fare?.penalties);

  const inclusionByType = {};
  inclusions.forEach((inclusion) => {
    const type = getInclusionType(inclusion?.descType);
    if (type && !inclusionByType[type]) inclusionByType[type] = inclusion;
  });

  const features = FEATURE_ORDER.map((type) => {
    const inclusion = inclusionByType[type];
    const inclusionSummary = inclusion
      ? getInclusionSummary(inclusion, type)
      : null;

    let summary = inclusionSummary;
    const isPenaltyType =
      type === FARE_FEATURE_TYPES.CANCELLATION ||
      type === FARE_FEATURE_TYPES.RESCHEDULE;

    if (isPenaltyType) {
      const penaltySummary = getPenaltySummary(
        getPenaltiesForType(penalties, type),
        fare,
      );
      // Slab wise penalties are the most precise source; an explicit
      // "not allowed"/"free" from inclusions still wins over amounts.
      if (
        penaltySummary &&
        (!inclusionSummary ||
          inclusionSummary.status === FARE_FEATURE_STATUS.CHARGEABLE ||
          inclusionSummary.status === FARE_FEATURE_STATUS.INFO)
      ) {
        summary = penaltySummary;
      }
    }

    if (!summary) summary = getLegacySummary(legacyInclusion, type);
    if (!summary) return null;

    return { type, label: FEATURE_LABELS[type], ...summary };
  }).filter(Boolean);

  const getBaggage = (type, fallback) => {
    const inclusion = inclusionByType[type];
    const parsed = parseBaggageFromText(inclusion?.descText);
    if (parsed) return parsed;
    const fallbackText = String(fallback ?? '').trim();
    // A bare unit with no number ("KG", "PC", "Check In: KG") carries no
    // information - show nothing rather than an empty allowance.
    if (fallbackText && /\d/.test(fallbackText)) return fallbackText;
    return '';
  };

  return {
    features,
    baggage: {
      checkin: getBaggage(FARE_FEATURE_TYPES.CHECKIN_BAGGAGE, checkinFallback),
      cabin: getBaggage(FARE_FEATURE_TYPES.CABIN_BAGGAGE, cabinFallback),
    },
  };
};

/** Baggage from fare inclusions only (used where a per leg fallback exists). */
export const getInclusionBaggage = (fare, type) => {
  const inclusion = toArray(fare?.inclusions).find(
    (item) => getInclusionType(item?.descType) === type,
  );
  return parseBaggageFromText(inclusion?.descText);
};

/**
 * Every inclusion sent for the fare, as a flat display list.
 * Status comes from isIncluded / isChargeable, falling back to the text.
 * @returns {Array<{ key: string, text: string, status: string }>}
 */
export const getFareInclusionList = (fare) => {
  const seen = new Set();
  return toArray(fare?.inclusions).reduce((list, inclusion) => {
    const descText = String(inclusion?.descText ?? '').trim();
    const descType = String(inclusion?.descType ?? '').trim();
    const text = descText && descText !== '-1' ? descText : descType;
    const key = text.toLowerCase();
    if (!text || seen.has(key)) return list;
    seen.add(key);

    const status =
      getStatusFromFlags(
        toTriState(inclusion?.isIncluded),
        toTriState(inclusion?.isChargeable),
      ) ||
      inferStatusFromText(text) ||
      FARE_FEATURE_STATUS.INFO;
    list.push({ key, text, status });
    return list;
  }, []);
};
